// Compliance Rules — per-box admin for the invoice/procurement rule engine.
// Toggle any rule off (non-applicable to this box) or change its severity.
// Rules flag; they never block. Nothing here traps a user.

import { useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';

type Rule = { code: string; name: string; description: string; severity: string; enabled: boolean; category: string; override_role: string };

const CAT_LABEL: Record<string, string> = {
  eligibility: 'Eligibility guardrails', match: 'Matching', reconciliation: 'Reconciliation', predictive: 'Predictive',
};
const CAT_ORDER = ['eligibility', 'match', 'reconciliation', 'predictive'];
const SEV: Record<string, { bg: string; fg: string }> = {
  blocker: { bg: '#fef2f2', fg: '#991b1b' }, warning: { bg: '#fffbeb', fg: '#92400e' }, info: { bg: '#eff6ff', fg: '#1e40af' },
};

export default function ComplianceRulesPage() {
  const qc = useQueryClient();
  const { data: rules = [] } = useQuery<Rule[]>({ queryKey: ['finance-rules'], queryFn: () => api.get('/finance/rules') });
  const mut = useMutation({ mutationFn: (v: { code: string; body: any }) => api.post(`/finance/rules/${v.code}/toggle`, v.body), onSuccess: () => qc.invalidateQueries({ queryKey: ['finance-rules'] }) });

  const byCat = useMemo(() => {
    const m: Record<string, Rule[]> = {};
    rules.forEach(r => { (m[r.category] ??= []).push(r); });
    return m;
  }, [rules]);
  const enabledN = rules.filter(r => r.enabled).length;

  return (
    <div>
      <div className="page__header" style={{ marginBottom: 8 }}>
        <h1 className="page__title" style={{ margin: 0, fontSize: 24 }}>Compliance Rules</h1>
        <div className="page__subtitle" style={{ color: '#64748b', fontSize: 13 }}>
          The rule engine that scores every invoice. Rules <b>flag</b> — they never block entry; each flag is overridable with a reason. Turn off any rule that doesn't apply to this operation.
        </div>
      </div>
      <div style={{ margin: '12px 0', fontSize: 12 }}>
        <span style={{ background: '#ecfeff', color: '#0e7490', padding: '3px 10px', borderRadius: 999, fontWeight: 600 }}>{enabledN} of {rules.length} rules active</span>
      </div>

      {CAT_ORDER.filter(c => byCat[c]?.length).map(cat => (
        <section key={cat} style={{ marginBottom: 20 }}>
          <h2 style={{ fontSize: 15, marginBottom: 8 }}>{CAT_LABEL[cat] ?? cat}</h2>
          <div style={{ background: '#fff', border: '1px solid var(--border,#e2e8f0)', borderRadius: 6, overflow: 'hidden' }}>
            {byCat[cat].map((r, i) => (
              <div key={r.code} style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '11px 14px', borderTop: i ? '1px solid #f1f5f9' : 'none', opacity: r.enabled ? 1 : 0.55 }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600, fontSize: 14 }}>{r.name} <span style={{ fontFamily: 'ui-monospace,monospace', fontSize: 10, color: '#cbd5e1', marginLeft: 4 }}>{r.code}</span></div>
                  <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>{r.description}</div>
                  <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 3 }}>Override authority: <b>{r.override_role}</b></div>
                </div>
                <select value={r.severity} onChange={e => mut.mutate({ code: r.code, body: { severity: e.target.value } })}
                  style={{ padding: '5px 8px', fontSize: 12, borderRadius: 6, border: 'none', fontWeight: 700, background: (SEV[r.severity] ?? SEV.info).bg, color: (SEV[r.severity] ?? SEV.info).fg, cursor: 'pointer' }}>
                  <option value="blocker">Blocker</option>
                  <option value="warning">Warning</option>
                  <option value="info">Info</option>
                </select>
                <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
                  <span style={{ position: 'relative', width: 38, height: 22, background: r.enabled ? '#16a34a' : '#cbd5e1', borderRadius: 999, transition: 'background .15s' }}
                    onClick={() => mut.mutate({ code: r.code, body: { enabled: !r.enabled } })}>
                    <span style={{ position: 'absolute', top: 2, left: r.enabled ? 18 : 2, width: 18, height: 18, background: '#fff', borderRadius: '50%', transition: 'left .15s' }} />
                  </span>
                  <span style={{ fontSize: 12, color: '#64748b', width: 44 }}>{r.enabled ? 'On' : 'Off'}</span>
                </label>
              </div>
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}
